// Plain copy for the notes a Boo's connection check returns (boo-brain
// src/llm/probe/checks.ts and verdicts.ts). The Boo's notes are written for
// engineers; each known one maps to a plain sentence here. A note this file
// doesn't recognise is hidden (null), so new wording never leaks through.

export const BUILT_IN_ONLY_NOTE = "Writing code uses built-in models only.";

// Rough pages of text for a number of tokens (about 650 tokens a page).
function pages(kTokens: number): string {
  const n = Math.max(1, Math.round((kTokens * 1000) / 650 / 10) * 10);
  return `about ${n} pages`;
}

export function plainCheckNote(id: string, status: string, note: string | undefined): string | null {
  if (!note || status === "skipped") return null;
  if (/localhost/i.test(note)) return "This address points at the Boo itself. Use an address reachable from the internet.";
  switch (id) {
    case "reachable":
      if (status === "pass") return null;
      if (/\(check the API key\)/.test(note)) return "The access key was refused. Check the key.";
      if (/\(check the model id/.test(note)) return "The model wasn't found. Check the model name and the address.";
      if (/^Endpoint not reachable/.test(note)) return "Couldn't reach this address. Check it, and that it's reachable from the internet.";
      if (/^Chat completion/.test(note)) return "The address answered, but the model didn't reply.";
      return null;
    case "latency": {
      const fast = /^Answered a short prompt in ([\d.]+) s/.exec(note);
      if (fast) return `Answers in about ${fast[1]} seconds.`;
      const slow = /^Took ([\d.]+) s/.exec(note);
      if (slow) return `Takes about ${slow[1]} seconds to answer, slower than built-in models.`;
      return null;
    }
    case "tool_calling":
      if (status === "pass") return null;
      if (/arguments did not match/.test(note)) return "Uses apps and tools, but sometimes gets the details wrong.";
      return "Can't use apps and tools.";
    case "structured_output":
      if (status === "pass") return null;
      if (/cannot enforce a schema/.test(note)) return "Fills in forms, but not always reliably.";
      return "Can't fill in forms reliably.";
    case "multi_turn_tools":
      if (status === "pass") return null;
      if (/did not use it/.test(note)) return "Sometimes loses track on long tasks with many steps.";
      if (/could not continue/.test(note)) return "Can't carry on after using an app or tool.";
      return null;
    case "context_window": {
      const both = /^Handled (\d+)k tokens; failed at (\d+)k/.exec(note);
      if (both) return `Handles ${pages(Number(both[1]))} at once; longer conversations get summarized more often.`;
      if (/missed a detail from its start/.test(note)) return "Can lose track of the start of long conversations.";
      if (/^Could not handle/.test(note)) return "Can't handle long documents; long conversations will fail.";
      const ok = /^Handled (\d+)k tokens of input/.exec(note);
      if (ok) return `Handles ${pages(Number(ok[1]))} at once.`;
      return null;
    }
    case "caching":
      if (status === "pass") return null;
      if (/No prompt caching/.test(note)) return "Long conversations cost more and reply more slowly.";
      return null;
    case "cost":
      if (/reports no cost/.test(note)) return "Spending on this model shows as unknown.";
      return null;
    case "refusal_signal":
      if (/no finish_reason/.test(note)) return "Boo can't tell when this model declines a request.";
      return null;
    default:
      return null;
  }
}

// The parts after "Works: " in a chat-job verdict.
const LOOP_NOTES: Array<[RegExp, string]> = [
  [/^no caching/, "long conversations cost more"],
  [/tokens fail, so long threads will break$/, "very long conversations will fail"],
  [/window, so long threads compact sooner$/, "long conversations get summarized sooner"],
  [/^slower than hosted models$/, "it's slower than built-in models"],
];

export function plainVerdictNote(purpose: string, note: string | undefined): string | null {
  if (purpose === "coding") return BUILT_IN_ONLY_NOTE;
  if (!note) return null;
  if (/^Good fit\.?$/i.test(note) || /^Works\.$/.test(note) || /^Serves embeddings/.test(note)) return null;
  if (/did not answer/.test(note)) return "Couldn't connect. Fix the address or the access key first.";
  if (/^Needs tool calling/.test(note)) return "Needs a model that can use apps and tools.";
  if (/^Needs structured answers/.test(note)) return "Needs a model that can fill in forms reliably.";
  if (/^Works, but slower than hosted models/.test(note)) return "Works, but slower than built-in models.";
  if (/schemas are not enforced/.test(note)) return "Works, but sometimes needs a second try.";
  if (/^No native decisions model/.test(note)) return "Works by asking this model for short answers.";
  if (/does not serve embeddings/.test(note)) return "Needs a model made for searching memory.";
  if (/transcription needs an audio model/.test(note)) return "Needs a voice-to-text model.";
  const works = /^Works: (.+)\.$/.exec(note);
  if (works) {
    const parts = works[1].split("; ").map((p) => LOOP_NOTES.find(([re]) => re.test(p))?.[1]);
    if (parts.every(Boolean)) return `Works, but ${parts.join(", and ")}.`;
    return null;
  }
  return null;
}
